// Small building blocks for the shop: pictures that pick the right size, things that rise into view, and scroll progress.
import React, { useEffect, useRef, useState } from 'react';
import { isVideo, storeImageSrcset, storeImageUrl } from './api.js';

// A store photo (or video). The browser gets a list of sizes and picks the smallest sharp one; the picture fades in
// once it has arrived. If a resized copy cannot be made it falls back to the file as uploaded.
export function Picture({ src, alt = '', sizes = '100vw', priority = false, className = '', style, fit }) {
  const [original, setOriginal] = useState(false), [loaded, setLoaded] = useState(false), [failed, setFailed] = useState(false);
  const image = useRef(null);
  useEffect(() => { setOriginal(false); setFailed(false); setLoaded(Boolean(image.current?.complete && image.current.naturalWidth)); }, [src]);
  if (!src || failed) return <div className={`shop-picture ${className}`} style={style} aria-hidden="true" />;
  if (isVideo(src)) return <div className={`shop-picture ${className}`} style={style}><video src={src} autoPlay muted loop playsInline preload="metadata" disablePictureInPicture style={{ width: '100%', height: '100%', objectFit: fit || 'cover' }} /></div>;
  const srcSet = original ? null : storeImageSrcset(src);
  return <div className={`shop-picture ${className}`} style={style}>
    <img ref={image} src={srcSet ? storeImageUrl(src, 1080) : src} srcSet={srcSet || undefined} sizes={srcSet ? sizes : undefined} alt={alt}
      className={loaded ? 'is-loaded' : ''} style={fit ? { objectFit: fit } : undefined}
      loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'auto'} decoding="async"
      onLoad={() => setLoaded(true)} onError={() => srcSet ? setOriginal(true) : setFailed(true)} />
  </div>;
}

// One shared observer: anything wrapped in <Reveal> rises into place the first time it scrolls into view.
let revealObserver = null;
const watchReveal = (element) => {
  // In a tab that is not being looked at, nothing would trigger the animation, so the content is simply shown.
  if (typeof IntersectionObserver === 'undefined' || document.hidden) { element.classList.add('is-in'); return () => {}; }
  revealObserver ||= new IntersectionObserver((entries) => {
    for (const entry of entries) if (entry.isIntersecting) { entry.target.classList.add('is-in'); revealObserver.unobserve(entry.target); }
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  revealObserver.observe(element);
  return () => revealObserver.unobserve(element);
};
export function Reveal({ as: Tag = 'div', i = 0, className = '', style, children, ...rest }) {
  const element = useRef(null);
  useEffect(() => watchReveal(element.current), []);
  return <Tag ref={element} className={`reveal ${className}`} style={{ ...style, '--i': i }} {...rest}>{children}</Tag>;
}

// Scroll progress for an element, written straight to its style as --p (0 to 1) so CSS can animate without re-rendering.
//   'exit'    0 when the element's top is at the top of the screen, 1 when it has scrolled away (heroes)
//   'through' 0 as it enters from below, 1 as it leaves at the top (parallax)
//   'pin'     0 to 1 across a tall element whose child is pinned to the screen (scroll stories)
const watchers = new Set();
let queued = false;
function measure() {
  queued = false;
  const screen = window.innerHeight;
  for (const watcher of watchers) {
    const box = watcher.element.getBoundingClientRect();
    const raw = watcher.mode === 'pin' ? -box.top / Math.max(1, box.height - screen) : watcher.mode === 'exit' ? -box.top / Math.max(1, box.height) : (screen - box.top) / (screen + box.height);
    const progress = Math.min(1, Math.max(0, raw));
    if (progress === watcher.last) continue;
    watcher.last = progress;
    watcher.element.style.setProperty('--p', progress.toFixed(4));
    watcher.callback.current?.(progress);
  }
}
const queue = () => { if (!queued) { queued = true; if (document.hidden) setTimeout(measure, 0); else requestAnimationFrame(measure); } };
export function useScrollProgress(ref, mode = 'through', onProgress) {
  const callback = useRef(onProgress);
  callback.current = onProgress;
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const watcher = { element, mode, callback, last: -1 };
    watchers.add(watcher);
    if (watchers.size === 1) { window.addEventListener('scroll', queue, { passive: true }); window.addEventListener('resize', queue); }
    queue();
    return () => { watchers.delete(watcher); if (!watchers.size) { window.removeEventListener('scroll', queue); window.removeEventListener('resize', queue); } };
  }, [ref, mode]);
}

export const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
